import { useCallback } from 'react'
import {
  DependencyItemType,
  useDependencyContext,
  useDependencyDragContextOptional,
} from './DependencyContext'

interface UseDependencyDragInteractionParams {
  rowIndex: number
  dependencyItemType: DependencyItemType | null
  barEndpoints: { startX: number | null; endX: number | null }
  isHovered: boolean
  isRowHovered: boolean
}

export const useDependencyDragInteraction = (
  params: UseDependencyDragInteractionParams
) => {
  const {
    rowIndex,
    dependencyItemType,
    barEndpoints,
    isHovered,
    isRowHovered,
  } = params
  const dragContext = useDependencyDragContextOptional()
  const dependencyContext = useDependencyContext()

  const isDependencyDragSource = dependencyItemType !== null
  const isDependencyDragging = dragContext?.dragState.isDragging ?? false
  const dragFromRowIndex = dragContext?.dragState.fromRowIndex ?? -1
  const dragFromItemType = dragContext?.dragState.fromItemType ?? null

  // Drop target: same item type only (Task↔Task, Deliverable↔Deliverable)
  const isPotentialDropTarget =
    isDependencyDragging &&
    rowIndex !== dragFromRowIndex &&
    rowIndex >= 0 &&
    dependencyItemType !== null &&
    dependencyItemType === dragFromItemType

  const showLeftDropTarget =
    isPotentialDropTarget && isRowHovered && barEndpoints.startX !== null

  const showRightDropTarget = false

  const showSourceCircles =
    dependencyContext !== null &&
    isDependencyDragSource &&
    isHovered &&
    !isDependencyDragging &&
    barEndpoints.endX !== null

  const handleConnectionMouseDown = useCallback(
    (event: React.MouseEvent, side: 'start' | 'end') => {
      if (!dragContext || !dependencyItemType || rowIndex < 0) return
      event.preventDefault()
      event.stopPropagation()
      dragContext.startDrag({
        fromRowIndex: rowIndex,
        fromSide: side,
        fromItemType: dependencyItemType,
        startX: event.clientX,
        startY: event.clientY,
      })
    },
    [dragContext, rowIndex, dependencyItemType]
  )

  const handleDropTargetMouseUp = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      if (!dragContext || !dependencyContext) return

      const fromRowIdx = dragContext.dragState.fromRowIndex
      if (fromRowIdx === null) return

      dependencyContext.addDependency({
        fromRowIndex: fromRowIdx,
        toRowIndex: rowIndex,
      })

      dragContext.endDrag()
    },
    [dragContext, dependencyContext, rowIndex]
  )

  return {
    isDependencyDragging,
    isPotentialDropTarget,
    showLeftDropTarget,
    showRightDropTarget,
    showSourceCircles,
    handleConnectionMouseDown,
    handleDropTargetMouseUp,
  }
}
